import React, { useState } from 'react';
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Menu,
    MenuItem,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useExternalSource, useEditExternalSource, useDeleteExternalSource } from '../../hooks/useExternalSource';
import { usePermission } from '../../hooks/usePermission';
import DataTable from '../shared/DataTable';
import { EditExternalSourcesPermission, DeleteExternalSourcesPermission } from '../../constants/permissions';

const ExternalSourceTable = () => {
    const { data: externalSources, isLoading } = useExternalSource();
    const editExternalSource = useEditExternalSource();
    const deleteExternalSource = useDeleteExternalSource();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedExternalSource, setSelectedExternalSource] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: '',
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const { data: canEditExternalSource } = usePermission({
        permissionName: EditExternalSourcesPermission
    });

    const { data: canDeleteExternalSource } = usePermission({
        permissionName: DeleteExternalSourcesPermission
    });

    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const handleEditClick = (externalSource) => {
        setSelectedExternalSource(externalSource);
        setEditFormData({
            name: externalSource.name,
            description: externalSource.description || '',
        });
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedExternalSource(null);
    };

    const handleEditSubmit = async () => {
        try {
            await editExternalSource.mutateAsync({
                id: selectedExternalSource.id,
                data: editFormData,
            });
            handleEditClose();
        } catch (error) {
            console.error('Error editing external source:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this external source?')) {
            try {
                await deleteExternalSource.mutateAsync(id);
                handleMenuClose();
            } catch (error) {
                console.error('Error deleting external source:', error);
            }
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '',
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={externalSources}
                searchFields={['name', 'description']}
                searchPlaceholder="Search external sources..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditExternalSource && (
                    <MenuItem onClick={() => handleEditClick(externalSources.find(es => es.id === selectedActionId))}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteExternalSource && (
                    <MenuItem onClick={() => handleDelete(selectedActionId)}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={editDialogOpen}
                onClose={handleEditClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit External Source</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    <div className="grid grid-cols-1 gap-4 p-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Name
                            </label>
                            <input
                                name="name"
                                value={editFormData.name}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, name: e.target.value })
                                }
                                type="text"
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={editFormData.description}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, description: e.target.value })
                                }
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                rows="4"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6 p-4">
                        <button
                            onClick={handleEditClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEditSubmit}
                            className="px-4 py-2 bg-blueColor text-white rounded"
                        >
                            Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ExternalSourceTable; 